import { CARD_WIDTH, CARD_HEIGHT, RESOLUTION } from '../utils/commonUtils';
import AssetsLoader from '../utils/assetsLoader';
import CardContainer from './cardContainer';
import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import CardHighlightFilter from '../filters/CardHighlightFilter';
import { getGlobalEventEmitter } from '../utils/globalEventEmitter';

const DON_DECK_POSITION = {
    x: 0,
    y: 0,
};

const ACTIVE_DONS_POSITION = {
    x: 178*RESOLUTION,
    y: -37*RESOLUTION,
};

const RESTED_DONS_POSITION = {
    x: 178*RESOLUTION,
    y: 0,
};

const OPPONENT_DON_DECK_POSITION = {
    x: 0,
    y: 0,
};

const OPPONENT_ACTIVE_DONS_POSITION = {
    x: -194*RESOLUTION,
    y: 80*RESOLUTION,
};

const OPPONENT_RESTED_DONS_POSITION = {
    x: -194*RESOLUTION,
    y: -241*RESOLUTION,
};

export default class DonsContainer extends PIXI.Container {
    constructor(playerState, isOpponent = false) {
        super();

        const activeDons = playerState.active_don.filter(don => !don.isRested);
        const restedDons = playerState.active_don.filter(don => don.isRested);

        const highlightFilter = new CardHighlightFilter();
        PIXI.Ticker.shared.add((ticker) => {
            highlightFilter.resources.timeUniforms.uniforms.uTime = ticker.lastTime * 0.0004;
        });

        /*-------- DON DECK --------*/
        const donDeckContainer = new PIXI.Container();
        donDeckContainer.label = 'donDeckContainer';
        const donDeckTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.DON_DECK);
        const donDeckSprite = new PIXI.Sprite(donDeckTexture);
        donDeckSprite.anchor.set(0.5, 1);
        donDeckSprite.x = isOpponent ? OPPONENT_DON_DECK_POSITION.x : DON_DECK_POSITION.x;
        donDeckSprite.y = isOpponent ? OPPONENT_DON_DECK_POSITION.y : DON_DECK_POSITION.y;
        if (isOpponent) {
            donDeckSprite.scale.x = -1;
        }
        donDeckContainer.addChild(donDeckSprite);

        const donDeckText = new PIXI.Text({
            text: playerState.don_deckCount,
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 45*RESOLUTION,
                fill: '#ffffff',
                stroke: { color: '#000000', width: 10*RESOLUTION },
            },
        });
        donDeckText.label = 'donDeckText';
        donDeckText.anchor.set(0.5, 1);
        donDeckText.x = !isOpponent ? 10*RESOLUTION : -10*RESOLUTION;
        donDeckText.y = -7*RESOLUTION;
        donDeckContainer.addChild(donDeckText);

        this.addChild(donDeckContainer);

        /*-------- ACTIVE DONS --------*/
        const activeDonsContainer = new PIXI.Container();
        activeDonsContainer.label = 'activeDonsContainer';
        activeDonsContainer.x = isOpponent ? OPPONENT_ACTIVE_DONS_POSITION.x : ACTIVE_DONS_POSITION.x;
        activeDonsContainer.y = isOpponent ? OPPONENT_ACTIVE_DONS_POSITION.y : ACTIVE_DONS_POSITION.y;

        const activeDonsFrameTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.ACTIVE_DONS_FRAME);
        const activeDonsFrameSprite = new PIXI.Sprite(activeDonsFrameTexture);
        activeDonsFrameSprite.anchor.set(0.5, 1);
        activeDonsFrameSprite.x = 0;
        activeDonsFrameSprite.y = 0;
        activeDonsContainer.addChild(activeDonsFrameSprite);

        const activeDonsHighlightTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.CARD_FADE);
        const activeDonsHighlightSprite = new PIXI.Sprite(activeDonsHighlightTexture);
        activeDonsHighlightSprite.label = 'activeDonsHighlight';
        activeDonsHighlightSprite.visible = false;
        activeDonsHighlightSprite.anchor.set(0.5, 0.5);
        activeDonsHighlightSprite.x = 13 * RESOLUTION;
        activeDonsHighlightSprite.y = -131 * RESOLUTION;
        activeDonsHighlightSprite.scale.set(0.46, 0.44);
        activeDonsHighlightSprite.filters = [highlightFilter];
        activeDonsContainer.addChild(activeDonsHighlightSprite);

        const activeDonsTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.ACTIVE_DONS);
        const activeDonsSprite = new PIXI.Sprite(activeDonsTexture);
        activeDonsSprite.anchor.set(0.5, 1);
        activeDonsSprite.x = 13*RESOLUTION;
        activeDonsSprite.y = -37*RESOLUTION;
        activeDonsContainer.addChild(activeDonsSprite);

        const activeDonsText = new PIXI.Text({
            text: activeDons.length,
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 50*RESOLUTION,
                fill: '#ffffff',
                stroke: { color: '#000000', width: 10*RESOLUTION },
            },
        });
        activeDonsText.label = 'activeDonsText';
        activeDonsText.anchor.set(0.5, 1);
        activeDonsText.x = 13*RESOLUTION;
        activeDonsText.y = -20*RESOLUTION;
        activeDonsContainer.addChild(activeDonsText);

        activeDonsContainer.eventMode = 'static';
        activeDonsContainer.on('pointerdown', (event) => {
            event.stopPropagation();
            getGlobalEventEmitter()?.emit('activeDonsClicked', playerState.playerId);
        });

        this.addChild(activeDonsContainer);
        
        /*-------- RESTED DONS --------*/
        const restedDonsContainer = new PIXI.Container();
        restedDonsContainer.label = 'restedDonsContainer';
        restedDonsContainer.x = isOpponent ? OPPONENT_RESTED_DONS_POSITION.x : RESTED_DONS_POSITION.x;
        restedDonsContainer.y = isOpponent ? OPPONENT_RESTED_DONS_POSITION.y : RESTED_DONS_POSITION.y;

        const restedDonsHighlightTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.CARD_FADE);
        const restedDonsHighlightSprite = new PIXI.Sprite(restedDonsHighlightTexture);
        restedDonsHighlightSprite.label = 'restedDonsHighlight';
        restedDonsHighlightSprite.visible = false;
        restedDonsHighlightSprite.anchor.set(0.5, 0.5);
        restedDonsHighlightSprite.x = 13*RESOLUTION;
        restedDonsHighlightSprite.y = 10*RESOLUTION;
        restedDonsHighlightSprite.angle = 90;
        restedDonsHighlightSprite.scale.set(0.46, 0.44);
        restedDonsHighlightSprite.filters = [highlightFilter];
        restedDonsContainer.addChild(restedDonsHighlightSprite);

        const restedDonsTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.RESTED_DONS);
        const restedDonsSprite = new PIXI.Sprite(restedDonsTexture);
        restedDonsSprite.anchor.set(0.5, 1);
        restedDonsSprite.x = 13*RESOLUTION;
        restedDonsSprite.y = 80*RESOLUTION;
        restedDonsContainer.addChild(restedDonsSprite);

        const restedDonsText = new PIXI.Text({
            text: restedDons.length,
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 50*RESOLUTION,
                fill: '#ffffff',
                stroke: { color: '#000000', width: 10*RESOLUTION },
            },
        });
        restedDonsText.label = 'restedDonsText';
        restedDonsText.anchor.set(0.5, 1);
        restedDonsText.x = 13*RESOLUTION;
        restedDonsText.y = 100*RESOLUTION;
        restedDonsContainer.addChild(restedDonsText);

        restedDonsContainer.eventMode = 'static';
        restedDonsContainer.on('pointerdown', (event) => {
            event.stopPropagation();
            getGlobalEventEmitter()?.emit('restedDonsClicked', playerState.playerId);
        });

        this.addChild(restedDonsContainer);

        const donCardsContainerForAnimation = new PIXI.Container();
        donCardsContainerForAnimation.label = 'donCardsContainerForAnimation';
        this.addChild(donCardsContainerForAnimation);

        const textRenderLayer = new PIXI.RenderLayer();
        textRenderLayer.label = 'textRenderLayer';
        this.addChild(textRenderLayer);

        textRenderLayer.attach(activeDonsText);
        textRenderLayer.attach(restedDonsText);

        const donCardForAnimation = new CardContainer({ type: 'DON!!' });
        donCardForAnimation.label = 'donCardForAnimation';
        donCardForAnimation.x = ACTIVE_DONS_POSITION.x;
        donCardForAnimation.y = ACTIVE_DONS_POSITION.y;
        donCardForAnimation.scale.set(0.6);
        donCardForAnimation.showFrontFace();
        donCardForAnimation.visible = false;
        this.addChild(donCardForAnimation);
    }

    update(playerState, clientGameState) {
        const activeDons = playerState.active_don.filter(don => !don.isRested);
        const restedDons = playerState.active_don.filter(don => don.isRested);

        const donDeckContainer = this.getChildByLabel('donDeckContainer');
        const donDeckText = donDeckContainer.getChildByLabel('donDeckText');
        donDeckText.text = playerState.don_deckCount;

        const activeDonsContainer = this.getChildByLabel('activeDonsContainer');
        const activeDonsText = activeDonsContainer.getChildByLabel('activeDonsText');
        activeDonsText.text = activeDons.length;

        const activeDonsHighlight = activeDonsContainer.getChildByLabel('activeDonsHighlight');
        activeDonsHighlight.visible = activeDons.some(don => clientGameState.selectableCards.includes(don.instanceId));

        const selectedActiveDonsForReturn = activeDons.filter(don => clientGameState.donsSelectedForReturn.includes(don.instanceId));
        if (selectedActiveDonsForReturn.length) {
            activeDonsText.text += `-${selectedActiveDonsForReturn.length}`;
        }

        const restedDonsContainer = this.getChildByLabel('restedDonsContainer');
        const restedDonsText = restedDonsContainer.getChildByLabel('restedDonsText');
        restedDonsText.text = restedDons.length;

        const restedDonsHighlight = restedDonsContainer.getChildByLabel('restedDonsHighlight');
        restedDonsHighlight.visible = restedDons.some(don => clientGameState.selectableCards.includes(don.instanceId));

        const selectedRestedDonsForReturn = restedDons.filter(don => clientGameState.donsSelectedForReturn.includes(don.instanceId));
        if (selectedRestedDonsForReturn.length) {
            restedDonsText.text += `-${selectedRestedDonsForReturn.length}`;
        }
    }

    incrementActiveDonsCounter(donsCount) {
        const activeDonsText = this.getChildByLabel('activeDonsText', true);
        activeDonsText.text = parseInt(activeDonsText.text) + donsCount;
    }

    decreaseActiveDonsCounter(donsCount) {
        const activeDonsText = this.getChildByLabel('activeDonsText', true);
        activeDonsText.text = parseInt(activeDonsText.text) - donsCount;
    }

    incrementRestedDonsCounter(donsCount) {
        const restedDonsText = this.getChildByLabel('restedDonsText', true);
        restedDonsText.text = parseInt(restedDonsText.text) + donsCount;
    }

    decreaseRestedDonsCounter(donsCount) {
        const restedDonsText = this.getChildByLabel('restedDonsText', true);
        restedDonsText.text = parseInt(restedDonsText.text) - donsCount;
    }

    async attachedDonAnimation(targetPosition, wasRested, isOpponent = false) {
        const donCardForAnimation = this.getChildByLabel('donCardForAnimation');
        donCardForAnimation.visible = true;
        donCardForAnimation.alpha = 0;
        donCardForAnimation.scale.set(0.6);
        donCardForAnimation.angle = 0;

        donCardForAnimation.x = 0;
        donCardForAnimation.y = 0;

        if (!wasRested) {
            if (isOpponent) {
                donCardForAnimation.x = -189;
                donCardForAnimation.y = 83;
            } else {
                donCardForAnimation.x = 189;
                donCardForAnimation.y = -83;
            }
        } else {
            if (isOpponent) {
                donCardForAnimation.x = -189;
                donCardForAnimation.y = -83;
            } else {
                donCardForAnimation.x = RESTED_DONS_POSITION.x;
                donCardForAnimation.y = RESTED_DONS_POSITION.y;
            }
        }

        const opacityTimeline = gsap.timeline();

        opacityTimeline.to(donCardForAnimation, {
            duration: 0.25,
            ease: 'power2.out',
            pixi: {
                alpha: 1,
            }
        });

        const scaleTimeline = gsap.timeline();
        scaleTimeline.to(donCardForAnimation, {
            duration: 0.5,
            pixi: {
                scale: 1,
            }
        });

        const rotationTimeline = gsap.timeline();
        rotationTimeline.to(donCardForAnimation, {
            duration: 0.5,
            pixi: {
                angle: isOpponent ? 15 : -15,
            }
        });

        const localTarget = this.toLocal(targetPosition);

        const positionTimeline = gsap.timeline();
        positionTimeline.to(donCardForAnimation, {
            duration: 0.5,
            pixi: {
                x: localTarget.x,
                y: localTarget.y,
            }
        });

        await Promise.all([opacityTimeline, scaleTimeline, rotationTimeline, positionTimeline]);

        donCardForAnimation.visible = false;
    }
}


